import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Modal, Button } from '@common/index';
import { Stack, TextField } from '@mui/material';

import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { flowActions, FlowEditModal, FlowModals, tabActions } from '@store/slices';
import { State } from '@store/index';
import { DeviceHubOutlined, Edit, Sensors } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { Label } from '@common/Label';
import { CreateNode } from '@components/Flow';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  & .MuiInputAdornment-root {
    margin-right: 1px;

    p {
      color: ${({ theme }) => theme.primary};
    }
  }

  #description {
    color: ${({ theme }) => theme.text.subtext};
    font-size: 13px;
    margin-top: 12px;
  }
`;

interface Props {
  addNode: ({ type, position }: CreateNode) => void;
}

export const CreateWorkflow: React.FC<Props> = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { service_id } = useParams();

  const flowEditModal = useSelector((state: State) => state.flow.modal) as FlowEditModal;

  const name = useSelector((state: State) => state.tabs.active.label);
  const isNewFlow = useSelector((state: State) => state.tabs.active.new);
  const isDuplicateFlow = useSelector((state: State) => state.tabs.active.duplicate);
  const flow_id = useSelector((state: State) => state.tabs.active.flow_id);
  const flows = useSelector((state: State) => state.flow.flows);

  const setName = (val: string) => dispatch(tabActions.setTabName(val));
  const modalOpen = flowEditModal?.type === FlowModals.EditFlow;
  const closeModal = () => dispatch(flowActions.setFlowModal(null));

  const [endpoint, setEndpoint] = useState(flow_id);

  const goBack = () => {
    if (!isNewFlow) return closeModal();
    dispatch(flowActions.setFlowModal({ type: FlowModals.SelectNewFlow, variant: 'workflow' }));
  };

  useEffect(() => {
    setEndpoint(flow_id);
  }, [flow_id]);

  const nameFlow = () => {
    if (!name) return;

    if (!isNewFlow) {
      dispatch(tabActions.setTabName(name));
      return closeModal();
    }

    const value = convert(endpoint);
    dispatch(tabActions.setServicePath(value));
    dispatch(tabActions.setTabName(name));

    dispatch(tabActions.setSelectedNode(null));
    dispatch(tabActions.completeTabCreation());
    dispatch(flowActions.setEndpoint(`/${service_id}/workflow`));
    closeModal();

    setTimeout(() => dispatch(flowActions.setBeginnerRightClickInfo(true)), 15000);
    queryClient.invalidateQueries({ queryKey: ['flow_info'] });
  };

  const nameFlowOnly = () => {
    if (!name) return;

    dispatch(tabActions.setTabName(name));
    return closeModal();
  };

  const convert = (input: string) => {
    let formatted = input.replace(/ /g, '_');
    formatted = formatted.replace(/[^a-zA-Z0-9_\-]/g, '');
    return formatted.toLowerCase();
  };

  const isInputError = (name.length < 3 || name.length > 64 || name.length === 0) && name;
  const isPathError = (endpoint.length < 3 || endpoint.length > 64 || endpoint.length === 0) && endpoint;

  const invalidServicePath = Boolean(
    flows.filter((flow) => flow.service_path === convert(endpoint) && flow.flow_id !== flow_id).length >= 1
  );

  if (!isNewFlow || isDuplicateFlow) {
    return (
      <Modal
        open={modalOpen}
        setOpen={closeModal}
        header={
          isDuplicateFlow && isNewFlow
            ? 'Duplicate Workflow'
            : isNewFlow && !isDuplicateFlow
            ? 'Create Workflow'
            : 'Update Name'
        }
        onClose={closeModal}
        icon={isNewFlow ? <Sensors /> : <Edit />}
        background='primary'
        noBottomBorder
        body={
          <Stack spacing='8px'>
            <Label title='Name' tooltip='A name for your endpoint.' />
            <TextField
              InputProps={{
                inputProps: {
                  'data-intercom-target': 'workflow-name-input-field',
                },
              }}
              size='small'
              fullWidth
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={name}
              error={Boolean(isInputError)}
              helperText={
                isInputError
                  ? `Must be ${name.length < 3 ? 'at least 3 characters' : 'less than 64 characters'}`
                  : undefined
              }
              placeholder={'A name for your endpoint'}
              onChange={(e) => setName(e.target.value)}
            />
          </Stack>
        }
        actions={
          <Stack direction='row' spacing={1}>
            <Button data-intercom-target='workflow-modal-back-button' small onClick={closeModal}>
              Back
            </Button>
            <Button small customColor='primary' onClick={nameFlowOnly} disabled={Boolean(isInputError || !name)}>
              Update
            </Button>
          </Stack>
        }
      />
    );
  }

  return (
    <Modal
      open={modalOpen}
      setOpen={goBack}
      header={isNewFlow ? 'Create Workflow' : 'Rename Workflow'}
      icon={isNewFlow ? <DeviceHubOutlined /> : <Edit />}
      background='primary'
      noBottomBorder
      info='Give your workflow a name to begin. You can add nodes by right clicking the canvas.'
      body={
        <Container>
          <Stack spacing='8px'>
            <Label title='Name' tooltip='A name for your workflow.' />
            <TextField
              InputProps={{
                inputProps: {
                  'data-intercom-target': 'workflow-name-input-field',
                },
              }}
              size='small'
              fullWidth
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={name}
              error={Boolean(isInputError)}
              helperText={
                isInputError
                  ? `Must be ${name.length < 3 ? 'at least 3 characters' : 'less than 64 characters'}`
                  : undefined
              }
              placeholder={'A name for your workflow'}
              onChange={(e) => setName(e.target.value)}
            />
          </Stack>
        </Container>
      }
      actions={
        <Stack direction='row' spacing={1}>
          <Button small onClick={goBack}>
            Back
          </Button>
          <Button
            small
            customColor='primary'
            onClick={nameFlow}
            disabled={Boolean(invalidServicePath || isInputError || isPathError || !name || !endpoint)}
          >
            {isNewFlow ? 'Create' : 'Update'}
          </Button>
        </Stack>
      }
    />
  );
};
